import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom"; // Import useLocation
import Head from "./Head";
import "./header.css";
import { FaHome, FaGraduationCap, FaInfoCircle, FaBook, FaUsers, FaPhone, FaChevronDown, FaInstagram } from "react-icons/fa";

const Header = () => {
  const [isAboutOpen, setIsAboutOpen] = useState(false);
  const [isProgramsOpen, setIsProgramsOpen] = useState(false);
  const [isAdmissionOpen, setIsAdmissionOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1100);
  const navigate = useNavigate();
  const location = useLocation(); // Get location object

  // Refs for click outside detection
  const navRef = useRef(null);
  const toggleButtonRef = useRef(null);

  // Handle dropdown toggle for mobile
  const handleDropdownToggle = (dropdownSetter, currentState) => {
    if (isMobile) {
      dropdownSetter(!currentState);
    }
  };

  // Resize handler
  useEffect(() => {
    const handleResize = () => {
      const mobileState = window.innerWidth <= 1100;
      setIsMobile(mobileState);
      if (!mobileState && mobileMenuOpen) { // Close menu if resizing larger while open
        setMobileMenuOpen(false);
        setIsAboutOpen(false);
        setIsProgramsOpen(false);
        setIsAdmissionOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check
    return () => window.removeEventListener('resize', handleResize);
  }, [mobileMenuOpen]); // Add mobileMenuOpen dependency

  // Close menu and dropdowns function
  const closeMenuAndDropdowns = () => {
    setMobileMenuOpen(false);
    setIsAboutOpen(false);
    setIsProgramsOpen(false);
    setIsAdmissionOpen(false);
  };

  // Handler for dropdown links
  const handleDropdownLinkClick = (e, path) => {
    navigate(path);
    closeMenuAndDropdowns(); // Close menu after navigation
  };

  // Handler for direct links (non-dropdown)
  const handleDirectLinkClick = () => {
    closeMenuAndDropdowns(); // Close menu on click
  };

  // Effect for click outside listener
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mobileMenuOpen &&
        navRef.current &&
        !navRef.current.contains(event.target) &&
        toggleButtonRef.current &&
        !toggleButtonRef.current.contains(event.target)
      ) {
        closeMenuAndDropdowns();
      }
    };

    if (mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  // Effect to close menu on route change
  useEffect(() => {
    if (mobileMenuOpen) {
      closeMenuAndDropdowns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Effect to toggle body class for menu push effect
  useEffect(() => {
    if (mobileMenuOpen && isMobile) { // Only apply on mobile when menu is open
      document.body.classList.add('mobile-menu-active');
      document.body.style.overflow = 'hidden'; // Prevent body scrolling
    } else {
      document.body.classList.remove('mobile-menu-active');
      document.body.style.overflow = ''; // Restore default scroll
    }

    // Cleanup function
    return () => {
      document.body.classList.remove('mobile-menu-active');
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen, isMobile]); // Re-run when menu state or mobile state changes

  return (
    <>
      <Head />

      {/* Top info bar - Hide on mobile ONLY when menu is open */}
      {!(isMobile && mobileMenuOpen) && (
        <div className="top-info-bar">
          <div className="container">
            <div className="contact-info">
              <span>Email: <a href="mailto:principal@papt.edu.in"> principal@papt.edu.in</a></span>
              <span>Phone:  +91 9964668925</span>
            </div>
            <div className="quick-links">
              <Link to="/contact" onClick={handleDirectLinkClick}>Admission Enquiry</Link>
              <Link to="/admission-procedure" onClick={handleDirectLinkClick}>Admission Procedure</Link>
              <Link to="/admission-open" onClick={handleDirectLinkClick}>Admission Open 2025</Link>
            </div>
          </div>
        </div>
      )}

      <header className="main-header">
        <div className="container header-container">
          <div className="logo-container">
            <Link to="/" className="logo-link" onClick={handleDirectLinkClick}>
              <img src="/images/PA_Polytechnic.jpg" alt="PA Polytechnic" className="logo" />
              <div className="college-name">
                <div className="college-title1">P A POLYTECHNIC</div>
                <div className="college-subtitle">NADUPADAV MANGALORE</div>
                <div className="college-subtitle">Approved by AICTE | Affiliated to BTE BANGALORE</div>
              </div>
            </Link>
          </div>

          {/* ✅ Instagram icon added here */}
          <div className="social-icon">
            <a
              href="https://www.instagram.com/pa_polytech?utm_source=ig_web_button_share_sheet&igsh=ZmtkYTRtaDFnbHdj"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="instagram-link"
            >
              <FaInstagram />
            </a>
          </div>

          <button
            ref={toggleButtonRef}
            className="mobile-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle mobile menu"
            aria-expanded={mobileMenuOpen}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>

          <nav ref={navRef} className={`main-nav ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <ul className="nav-list">
              <li className="nav-item">
                <Link to="/" className="nav-link" onClick={handleDirectLinkClick}>
                  <FaHome className="nav-icon" />
                  <span>Home</span>
                </Link>
              </li>

              {/* About Dropdown */}
              <li className={`nav-item dropdown ${isAboutOpen ? 'open' : ''}`}
                onMouseEnter={!isMobile ? () => setIsAboutOpen(true) : null}
                onMouseLeave={!isMobile ? () => setIsAboutOpen(false) : null}>
                <div className="nav-link dropdown-toggle" onClick={() => isMobile && handleDropdownToggle(setIsAboutOpen, isAboutOpen)}>
                  <FaInfoCircle className="nav-icon" />
                  <span>About</span>
                  {isMobile && <FaChevronDown className={`dropdown-chevron ${isAboutOpen ? 'open' : ''}`} />}
                </div>
                {isAboutOpen && (
                  <div className="mega-dropdown">
                    <div className="dropdown-section">
                      <ul>
                        <li><Link to="/history" onClick={(e) => handleDropdownLinkClick(e, '/history')}>History of PACE group</Link></li>
                        <li><Link to="/papt" onClick={(e) => handleDropdownLinkClick(e, '/papt')}>About PAPT</Link></li>
                        <li><Link to="/chairman" onClick={(e) => handleDropdownLinkClick(e, '/chairman')}>Founder/Chairman</Link></li>
                        <li><Link to="/managingtrustee" onClick={(e) => handleDropdownLinkClick(e, '/managingtrustee')}>Managing Trustee</Link></li>
                        <li><Link to="/mission" onClick={(e) => handleDropdownLinkClick(e, '/mission')}>Vision and Mission</Link></li>
                        <li><Link to="/principal" onClick={(e) => handleDropdownLinkClick(e, '/principal')}>Principal's Message</Link></li>
                        <li><Link to="/administration" onClick={(e) => handleDropdownLinkClick(e, '/administration')}>Administration</Link></li>
                        <li><Link to="/leadership" onClick={(e) => handleDropdownLinkClick(e, '/leadership')}>Board of Trustees</Link></li>
                      </ul>
                    </div>
                  </div>
                )}
              </li>

              {/* Programs Dropdown */}
              <li className={`nav-item dropdown ${isProgramsOpen ? 'open' : ''}`}
                onMouseEnter={!isMobile ? () => setIsProgramsOpen(true) : null}
                onMouseLeave={!isMobile ? () => setIsProgramsOpen(false) : null}>
                <div className="nav-link dropdown-toggle" onClick={() => isMobile && handleDropdownToggle(setIsProgramsOpen, isProgramsOpen)}>
                  <FaGraduationCap className="nav-icon" />
                  <span>Programs</span>
                  {isMobile && <FaChevronDown className={`dropdown-chevron ${isProgramsOpen ? 'open' : ''}`} />}
                </div>
                {isProgramsOpen && (
                  <div className="mega-dropdown">
                    <div className="dropdown-section">
                      <h3>Engineering</h3>
                      <ul>
                        <li><Link to="/civilengineeruser" onClick={(e) => handleDropdownLinkClick(e, '/civilengineeruser')}>Civil Engineering</Link></li>
                        <li><Link to="/computerscienceuser" onClick={(e) => handleDropdownLinkClick(e, '/computerscienceuser')}>Computer Science</Link></li>
                        <li><Link to="/electricalengineeruser" onClick={(e) => handleDropdownLinkClick(e, '/electricalengineeruser')}>Electrical and Electronics</Link></li>
                        <li><Link to="/electronicsengineeruser" onClick={(e) => handleDropdownLinkClick(e, '/electronicsengineeruser')}>Electronics and Communication</Link></li>
                        <li><Link to="/mechanicalengineeruser" onClick={(e) => handleDropdownLinkClick(e, '/mechanicalengineeruser')}>Mechanical Engineering</Link></li>
                      </ul>
                    </div>
                    <div className="dropdown-section">
                      <h3>Basic Science & Humanities</h3>
                      <ul>
                        <li><Link to="/basicscienceuser" onClick={(e) => handleDropdownLinkClick(e, '/basicscienceuser')}>Basic Science</Link></li>
                        <li><Link to="/physicaleducationuser" onClick={(e) => handleDropdownLinkClick(e, '/physicaleducationuser')}>Physical Education</Link></li>
                      </ul>
                    </div>
                  </div>
                )}
              </li>

              {/* Admission Dropdown */}
              <li className={`nav-item dropdown ${isAdmissionOpen ? 'open' : ''}`}
                onMouseEnter={!isMobile ? () => setIsAdmissionOpen(true) : null}
                onMouseLeave={!isMobile ? () => setIsAdmissionOpen(false) : null}>
                <div className="nav-link dropdown-toggle" onClick={() => isMobile && handleDropdownToggle(setIsAdmissionOpen, isAdmissionOpen)}>
                  <FaUsers className="nav-icon" />
                  <span>Admission</span>
                  {isMobile && <FaChevronDown className={`dropdown-chevron ${isAdmissionOpen ? 'open' : ''}`} />}
                </div>
                {isAdmissionOpen && (
                  <div className="mega-dropdown">
                    <div className="dropdown-section">
                      <ul>
                        <li><Link to="/courses" onClick={(e) => handleDropdownLinkClick(e, '/courses')}>Courses</Link></li>
                        <li><Link to="/admission/transport" onClick={(e) => handleDropdownLinkClick(e, '/admission/transport')}>Transportation</Link></li>
                        <li><Link to="/admission/hostel" onClick={(e) => handleDropdownLinkClick(e, '/admission/hostel')}>Hostels</Link></li>
                        <li><Link to="/admission/library" onClick={(e) => handleDropdownLinkClick(e, '/admission/library')}>Library</Link></li>
                        <li><Link to="/canteen" onClick={(e) => handleDropdownLinkClick(e, '/canteen')}>Canteen/Mess</Link></li>
                        <li><Link to="/admission/fitness" onClick={(e) => handleDropdownLinkClick(e, '/admission/fitness')}>Sports and Fitness</Link></li>
                        <li><Link to="/admission/classrooms" onClick={(e) => handleDropdownLinkClick(e, '/admission/labs')}>Labs/Classrooms</Link></li>
                        <li><Link to="/SeminarHall" onClick={(e) => handleDropdownLinkClick(e, '/SeminarHall')}>Seminar Halls</Link></li>
                      </ul>
                    </div>
                  </div>
                )}
              </li>

              {/* Direct Links */}
              <li className="nav-item">
                <Link to="/publicationuser" className="nav-link" onClick={handleDirectLinkClick}>
                  <FaBook className="nav-icon" />
                  <span>Publications</span>
                </Link>
              </li>

              <li className="nav-item">
                <Link to="/gallery" className="nav-link" onClick={handleDirectLinkClick}>
                  <FaBook className="nav-icon" />
                  <span>Gallery</span>
                </Link>
              </li>

              <li className="nav-item">
                <Link to="/news" className="nav-link" onClick={handleDirectLinkClick}>
                  <FaBook className="nav-icon" />
                  <span>News</span>
                </Link>
              </li>

              <li className="nav-item">
                <Link to="/contact" className="nav-link" onClick={handleDirectLinkClick}>
                  <FaPhone className="nav-icon" />
                  <span>Contact</span>
                </Link>
              </li>
            </ul>
          </nav>
        </div>
      </header>
    </>
  );
};

export default Header;
